import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  createSponsorImpression,
  createSponsorClick,
  trackAdPixel,
} from "core/utils/tracker";
import ButtonBase from "@mui/material/ButtonBase";
import { getThumbnailSrcSet } from "core/utils/getSrcSet";
import { nanoid } from "nanoid";
import {
  trackBrandingEvent,
  BRANDING_IMPRESSION,
  BRANDING_CLICK,
} from "core/utils/adAnalytics";

const SHOW_BANNER_TIMEOUT = 10000; // Через сколько мс закрывать баннер
const ANIMATION_DURATION = 1000; // Длительность анимации (как у поджатия видео в PlayerInView)

// Плеер уже этого считаем узким (телефон): показываем мобильный вариант креатива, если он есть
export const MOBILE_PLAYER_MAX_WIDTH = 600;

const IMPRESSION = "IMPRESSION";
const DEFAULT = "DEFAULT";
const MEDIUM = "MEDIUM";
const HIGH = "HIGH";
const MAXRES = "MAXRES";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const Root = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "visible",
})(({ visible }) => ({
  display: "block",
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  zIndex: 100,
  // Выезжает снизу синхронно с поджатием видео и уезжает обратно, без
  // промежуточного состояния, когда видна чернота
  transform: visible ? "translateY(0)" : "translateY(100%)",
  // Подстраховка, если контейнер плеера не обрезает содержимое: в скрытом
  // состоянии баннер ещё и прозрачный и не перехватывает клики
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? "auto" : "none",
  transition: `transform ${ANIMATION_DURATION}ms ease, opacity ${ANIMATION_DURATION}ms ease`,
  willChange: "transform, opacity",
}));

const StyledImage = styled("img")({
  width: "100%",
  display: "block",
  margin: 0,
});

/**
 * Креатив под ширину плеера: в узком плеере мобильный вариант (если загружен)
 */
export function pickThumbnails({ thumbnails, mobileThumbnails, playerWidth }) {
  if (
    playerWidth > 0 &&
    playerWidth < MOBILE_PLAYER_MAX_WIDTH &&
    mobileThumbnails?.length
  ) {
    return mobileThumbnails;
  }

  return thumbnails || [];
}

export default function SponsorInStreamBanner({
  onStart,
  onFinish,
  inStreamBanner,
  setInStreamBannerHeight,
}) {
  const rootRef = useRef();
  const imageRef = useRef();
  const timersRef = useRef([]);
  const [playerWidth, setPlayerWidth] = useState(0);
  const [visible, setVisible] = useState(false);

  const {
    thumbnails,
    mobileThumbnails,
    link,
    token,
    isClickable,
    clickPixel,
    impressionPixel,
  } = inStreamBanner;

  // Ширина плеера: креатив выбираем до загрузки картинки
  useIsomorphicLayoutEffect(() => {
    const player = rootRef.current?.parentElement;
    if (player) setPlayerWidth(player.offsetWidth);
  }, []);

  useEffect(() => {
    const onWindowResize = () => {
      if (imageRef.current && visible) {
        setInStreamBannerHeight(imageRef.current.height);
      }
    };

    window.addEventListener("resize", onWindowResize);

    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, [visible, setInStreamBannerHeight]);

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      setInStreamBannerHeight(null);
    },
    [setInStreamBannerHeight]
  );

  // Один clickId на показ: ссылка готова заранее, клик идёт обычной навигацией
  // по <a target="_blank">. window.open WebView приложений (iOS) молча
  // игнорирует, поэтому баннер «не кликался».
  const clickId = useMemo(() => nanoid(), []);
  const href =
    isClickable && link ? link.replace("{clickId}", clickId) : undefined;

  const onClick = (event) => {
    if (!href) {
      event.preventDefault();
      return;
    }

    if (clickPixel) {
      trackAdPixel(clickPixel);
    }

    trackBrandingEvent(BRANDING_CLICK, { format: "strip" });

    createSponsorClick(token, clickId);
  };

  const onLoad = () => {
    createSponsorImpression(token, "In-Stream Banner", IMPRESSION);

    trackBrandingEvent(BRANDING_IMPRESSION, { format: "strip" });

    if (impressionPixel) {
      trackAdPixel(impressionPixel);
    }

    onStart();

    if (imageRef.current) {
      setInStreamBannerHeight(imageRef.current.height);
    }

    setVisible(true);

    timersRef.current.push(
      setTimeout(() => {
        setVisible(false);
        setInStreamBannerHeight(null);
      }, SHOW_BANNER_TIMEOUT - ANIMATION_DURATION),
      setTimeout(() => {
        onFinish();
      }, SHOW_BANNER_TIMEOUT)
    );
  };

  const selected = pickThumbnails({
    thumbnails,
    mobileThumbnails,
    playerWidth,
  });
  const srcSet = getThumbnailSrcSet(selected);
  const src = selected.find((t) => t.format === MAXRES)?.url;

  const linkProps = href
    ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
    : { component: "div" };

  return (
    <Root ref={rootRef} visible={visible} onClick={onClick} {...linkProps}>
      {playerWidth > 0 && src && (
        <StyledImage
          src={src}
          srcSet={srcSet}
          alt=""
          onLoad={onLoad}
          ref={imageRef}
        />
      )}
    </Root>
  );
}

SponsorInStreamBanner.propTypes = {
  inStreamBanner: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    mobileThumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    link: PropTypes.string,
    token: PropTypes.string.isRequired,
    isClickable: PropTypes.bool,
    clickPixel: PropTypes.string,
    impressionPixel: PropTypes.string,
  }),
  setInStreamBannerHeight: PropTypes.func,
  onStart: PropTypes.func,
  onFinish: PropTypes.func,
};
